import { Feedback } from "./Feedback";
import { LanguageSelector, translate, useLanguage } from "./language";
/**
 * The header every app shares: brand lockup, app nav, theme switcher, account
 * menu. Lives here rather than in each SPA so the three apps cannot drift.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { BrandLockup, ThemeToggle } from "@cqt/brand";
import { useAuth } from "./auth";

export interface NavItem {
  label: string;
  /** Absolute path under the platform host, or an in-app route. */
  href: string;
  /** True when this item is the current page. */
  current?: boolean;
}

function initials(name: string | null, email: string): string {
  const source = (name ?? "").trim() || email;
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  return (parts[0]?.[0] ?? "?").concat(parts[1]?.[0] ?? "").toUpperCase();
}

function AccountMenu() {
  useLanguage();
  const { me, logout, registerPasskey, accountUrl, config } = useAuth();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrap} style={{ position: "relative" }}>
      <button
        className="avatar"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={translate("Account menu")}
        onClick={() => setOpen((v) => !v)}
      >
        {initials(me.user.full_name, me.user.email)}
      </button>

      {open ? (
        <div className="menu" role="menu">
          <div className="menu__head">
            <div className="menu__name">{me.user.full_name ?? me.user.email}</div>
            <div className="menu__sub mono">{me.user.email}</div>
            <div className="menu__sub" style={{ marginTop: 6 }}>
              {me.company?.name ?? translate("Your companies")}
              {me.is_superadmin ? <span className="chip chip--accent" style={{ marginLeft: 8 }}>{translate("Superadmin")}</span> : null}
            </div>
          </div>
          {/* One login, many companies. Each entry is a LINK to that company's
              own address rather than a switch the server performs: the address
              is the tenancy boundary, so changing company means going there.
              Only shown when there is somewhere else to go. */}
          {me.companies.filter((c) => c.id !== me.company?.id && c.address).length > 0 ? (
            <div className="menu__body" style={{ borderBottom: "1px solid var(--border)" }}>
              <div className="menu__row" style={{ alignItems: "flex-start" }}>
                <span className="label" style={{ letterSpacing: "0.12em" }}>{translate("Switch to")}</span>
                <span style={{ display: "grid", gap: 4, justifyItems: "end" }}>
                  {me.companies
                    .filter((c) => c.id !== me.company?.id && c.address)
                    .map((c) => (
                      <a key={c.id} href={`https://${c.address}/`} className="menu__link">
                        {c.name} →
                      </a>
                    ))}
                </span>
              </div>
            </div>
          ) : null}
          <div className="menu__body">
            <LanguageSelector />
            <div className="menu__row">
              <span className="label" style={{ letterSpacing: "0.12em" }}>{translate("Theme")}</span>
              <ThemeToggle translateLabel={translate} />
            </div>
            {me.roles.length > 0 ? (
              <div className="menu__row" style={{ alignItems: "flex-start" }}>
                <span className="label" style={{ letterSpacing: "0.12em" }}>{translate("Roles")}</span>
                <span style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {me.roles.map((r) => (
                    <span key={r.id} className="chip">{r.name}</span>
                  ))}
                </span>
              </div>
            ) : null}
          </div>
          <div className="menu__foot">
            <button className="btn btn--ghost btn--sm" role="menuitem" onClick={() => void registerPasskey()}>
               {translate("Set up passkey")} </button>
            <a className="btn btn--ghost btn--sm" href={accountUrl} target="_blank" rel="noreferrer noopener">
              {config.authMode === "platform" ? translate("Account security") : translate("Keycloak account")}
            </a>
            <button className="btn btn--outline btn--sm" role="menuitem" onClick={logout}>
               {translate("Sign out")} </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * @param app     short app label shown next to the wordmark, e.g. "CRM"
 * @param nav     header links; omit for the portal
 * @param actions extra controls rendered before the account menu
 */
export function AppShell({
  app,
  nav = [],
  actions,
  children,
}: {
  app?: string;
  nav?: NavItem[];
  actions?: ReactNode;
  children: ReactNode;
}) {
  const { language } = useLanguage();
  const { config } = useAuth();
  // The company's display name, with the product name as the fallback for a
  // company that has not been given one.
  const company = config.companyName || "Platform";

  // The static <title> in each index.html is the neutral app name, because the
  // company is not known until /config.json is fetched. Filling it in here means
  // a bookmark and a browser tab name the company rather than the product — and
  // means no app ships one customer's name in its HTML.
  useEffect(() => {
    document.title = app ? `${company} — ${translate(app)}` : company;
  }, [company, app, language]);

  return (
    <div className="app-shell">
      <header className="plate appbar">
        <BackToCompanies />
        <a
          href="/"
          style={{ textDecoration: "none" }}
          aria-label={translate("{company} home", {company})}
        >
          {/* The company's own wordmark and logo. Hardcoding one customer's
              name here branded every company — and the platform-wide admin
              panel — as that customer. */}
          <BrandLockup
            app={app ? translate(app) : app}
            wordmark={config.brand.wordmark || company}
            logoUrl={config.brand.logoUrl}
          />
        </a>

        {nav.length > 0 ? (
          <nav className="appbar__nav" aria-label={translate("Sections")}>
            {nav.map((item) => (
              <a
                key={item.href}
                className="appbar__link"
                href={item.href}
                aria-current={item.current ? "page" : undefined}
              >
                {translate(item.label)}
              </a>
            ))}
          </nav>
        ) : null}

        <div className="spacer" />
        {actions}
        {/* The switcher is also in the account menu; keeping it in the header
            makes it a one-click change, which is what people actually want. */}
        <ThemeToggle onPlate translateLabel={translate} />
        <AccountMenu />
      </header>

      <main className="app-main">{children}</main>
      <Feedback application={app} />
    </div>
  );
}

/**
 * Back to the list of companies, for somebody who has more than one.
 *
 * Not a browser Back: entering a company means going to its address, so the way
 * out is going to the front door's address, and history may not contain it —
 * somebody who bookmarked this company, or opened it in a new tab, has nothing
 * to go back TO. It is a link, for the same reason the picker's cards are links.
 *
 * Shown only when there is more than one company to return to. With exactly
 * one, the account home has a single card on it and the trip is a pointless
 * hop; with none, the person is already there. The account menu keeps its
 * "Switch to" list, which is the sideways move — naming the company you want.
 * This is the way back, for when you want to see them all.
 */
function BackToCompanies() {
  useLanguage();
  const { config, me } = useAuth();
  if (!config.accountHome || config.isPlatformHost) return null;
  if (!me || me.companies.length < 2) return null;
  return (
    <a
      className="appbar__back"
      href={`https://${config.accountHome}/`}
      aria-label={translate("Back to your companies")}
      title={translate("Your companies")}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M15 18l-6-6 6-6" />
      </svg>
      <span className="appbar__back-label">{translate("Companies")}</span>
    </a>
  );
}

/** Page heading with an optional eyebrow and right-aligned actions. */
export function PageHeader({
  eyebrow,
  title,
  actions,
}: {
  /** ReactNode rather than string so a page can put a breadcrumb here. Widened,
   *  so every existing caller passing a string still type-checks. */
  eyebrow?: ReactNode;
  title: ReactNode;
  actions?: ReactNode;
}) {
  useLanguage();
  return (
    <div className="row-between" style={{ marginBottom: 22, flexWrap: "wrap", gap: 12 }}>
      <div>
        {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
        <h1>{title}</h1>
      </div>
      {actions ? (
        // Wrapping, and scoped to this container rather than `.row` globally.
        // `.row` is nowrap, so a header with several controls — the CRM gained a
        // product-line switcher — grew past the viewport and clipped its primary
        // button while the page scrolled sideways. Measured at 775px: the row
        // wanted 827px.
        <div className="row" style={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
          {actions}
        </div>
      ) : null}
    </div>
  );
}
